import { API_BASE, api } from "../api";

export const loadActiveCatalogProducts = async () => {
  const response = await api.searchProducts("");
  const seen = new Set();

  return (response.products || []).filter((product) => {
    if (!product?.id || seen.has(product.id)) return false;
    if (product.isActive === 0 || product.isActive === false) return false;
    seen.add(product.id);
    return true;
  });
};

const parseRichText = (html) => {
  if (!html || typeof DOMParser === "undefined") return [];

  const parsed = new DOMParser().parseFromString(String(html), "text/html");
  const blocks = [];
  let runs = [];

  const flush = () => {
    runs = runs
      .map((run) => ({ ...run, text: run.text.replace(/\s+/g, " ") }))
      .filter((run) => run.text.trim());
    if (runs.length) blocks.push({ type: "text", runs });
    runs = [];
  };

  const appendText = (text, style) => {
    if (!text) return;
    const pieces = text.split(/(\r?\n)/);
    pieces.forEach((piece) => {
      if (/^\r?\n$/.test(piece)) {
        flush();
      } else if (piece) {
        runs.push({ text: piece, ...style });
      }
    });
  };

  const visit = (node, style = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendText(node.nodeValue, style);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();
    const blockElement = /^(p|div|h[1-6]|ul|ol|table|tr)$/.test(tag);
    if (blockElement) flush();

    if (tag === "br") {
      flush();
      return;
    }
    if (tag === "img") {
      flush();
      const src = node.getAttribute("src");
      if (src) blocks.push({ type: "image", src });
      return;
    }
    if (tag === "li") {
      flush();
      appendText("- ", {});
    }
    if (tag === "td" || tag === "th") {
      flush();
    }

    const nextStyle = {
      bold: style.bold || /^(b|strong|th)$/.test(tag) || /^h[1-6]$/.test(tag),
      italic: style.italic || /^(i|em)$/.test(tag)
    };
    Array.from(node.childNodes).forEach((child) => visit(child, nextStyle));

    if (tag === "td" || tag === "th") appendText("  ", {});
    if (blockElement || tag === "li" || tag === "td" || tag === "th") flush();
  };

  Array.from(parsed.body.childNodes).forEach((node) => visit(node));
  flush();
  return blocks;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return "";
  let path = imagePath;
  if (/^https?:\/\//i.test(path)) {
    const imageUrl = new URL(path);
    if (!["localhost", "127.0.0.1"].includes(imageUrl.hostname)) return path;
    path = `${imageUrl.pathname}${imageUrl.search}`;
  }

  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (isLocalhost && path.startsWith("/backend/uploads/")) {
    path = path.replace(/^\/backend/, "");
  } else if (!isLocalhost && path.startsWith("/uploads/")) {
    path = `/backend${path}`;
  }
  return new URL(path, API_BASE || window.location.origin).href;
};

const loadImage = async (imagePath, productName) => {
  try {
    const response = await fetch(getImageUrl(imagePath));
    if (!response.ok) throw new Error(`Image request failed (${response.status})`);

    const bitmap = await createImageBitmap(await response.blob());
    const scale = Math.min(1, 500 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare catalogue image");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    return {
      data: canvas.toDataURL("image/jpeg", 0.42),
      width: canvas.width,
      height: canvas.height
    };
  } catch (error) {
    console.warn(`Unable to load catalogue image for "${productName}"`, error);
    return null;
  }
};

const loadProductImages = async (product) => {
  const imagePaths = [product.image, ...(product.images || []).map((image) => image.image)]
    .filter((imagePath, index, allPaths) => imagePath && allPaths.indexOf(imagePath) === index);

  return Promise.all(imagePaths.map((imagePath) => loadImage(imagePath, product.name)));
};

const loadRichTextImages = async (blocks, productName) =>
  Promise.all(
    blocks.map((block) =>
      block.type === "image" ? loadImage(block.src, productName) : Promise.resolve(null)
    )
  );

const fontStyle = (run) => {
  if (run.bold && run.italic) return "bolditalic";
  if (run.bold) return "bold";
  if (run.italic) return "italic";
  return "normal";
};

const wrapRichText = (document, runs, maxWidth) => {
  const lines = [];
  let line = [];
  let lineWidth = 0;

  runs.forEach((run) => {
    const parts = run.text.split(/(\s+)/).filter(Boolean);
    parts.forEach((part) => {
      document.setFont("helvetica", fontStyle(run));
      let remaining = part;
      while (remaining) {
        let candidate = remaining;
        while (candidate.length > 1 && document.getTextWidth(candidate) > maxWidth) {
          candidate = candidate.slice(0, -1);
        }
        const width = document.getTextWidth(candidate);
        if (lineWidth + width > maxWidth && line.length) {
          lines.push(line);
          line = [];
          lineWidth = 0;
          if (/^\s+$/.test(candidate)) return;
        }
        line.push({ ...run, text: candidate });
        lineWidth += width;
        remaining = remaining.slice(candidate.length);
      }
    });
  });
  if (line.length) lines.push(line);
  return lines;
};

const drawImageContained = (document, image, x, y, width, height) => {
  if (!image) {
    document.setFont("helvetica", "normal");
    document.setFontSize(9);
    document.setTextColor(120, 132, 145);
    document.text("Product image unavailable", x + width / 2, y + height / 2, {
      align: "center",
      maxWidth: width - 8
    });
    return;
  }

  const scale = Math.min(width / image.width, height / image.height);
  const imageWidth = image.width * scale;
  const imageHeight = image.height * scale;
  document.addImage(
    image.data,
    "JPEG",
    x + (width - imageWidth) / 2,
    y + (height - imageHeight) / 2,
    imageWidth,
    imageHeight,
    undefined,
    "FAST"
  );
};

const drawPageFooter = (document, pageNumber, pageCount) => {
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  document.setDrawColor(218, 226, 233);
  document.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
  document.setFont("helvetica", "normal");
  document.setFontSize(8);
  document.setTextColor(100, 116, 130);
  document.text("RK Industries | Laboratory & Scientific Equipment", 15, pageHeight - 9);
  document.text(`${pageNumber} / ${pageCount}`, pageWidth - 15, pageHeight - 9, {
    align: "right"
  });
};

const drawSectionHeading = (document, title, x, y) => {
  document.setFont("helvetica", "bold");
  document.setFontSize(11);
  document.setTextColor(0, 130, 150);
  document.text(title, x, y);
  document.setDrawColor(0, 153, 170);
  document.setLineWidth(0.45);
  document.line(x, y + 3, x + 34, y + 3);
};

const addProductPageHeader = (document, product, continuation = false) => {
  const pageWidth = document.internal.pageSize.getWidth();
  document.setFillColor(17, 45, 73);
  document.rect(0, 0, pageWidth, 23, "F");
  document.setFillColor(0, 153, 170);
  document.rect(0, 23, pageWidth, 1.5, "F");
  document.setFont("helvetica", "bold");
  document.setFontSize(9);
  document.setTextColor(255, 255, 255);
  document.text("RK INDUSTRIES INDIA", 15, 10);
  document.setFont("helvetica", "normal");
  document.setFontSize(8);
  document.text("+91 8685933785", 15, 17);
  document.setFontSize(10);
  document.text(
    continuation ? "PRODUCT DETAILS | CONTINUED" : "PRODUCT CATALOGUE",
    pageWidth - 15,
    14,
    { align: "right" }
  );
  if (continuation) {
    document.setFont("helvetica", "bold");
    document.setFontSize(11);
    document.setTextColor(17, 45, 73);
    document.text(
      document.splitTextToSize(product.name || "Product", pageWidth - 30).slice(0, 2),
      15,
      36
    );
  }
};

const drawTextSection = (document, {
  title,
  blocks,
  images,
  product,
  pageWidth,
  pageHeight,
  getY,
  setY,
  startNewPage
}) => {
  let y = getY();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  if (y + 14 > pageHeight - 22) {
    y = startNewPage();
  }

  drawSectionHeading(document, title, margin, y);
  y += 9;

  if (blocks.length === 0) {
    blocks = [{ type: "text", runs: [{ text: "Not provided." }] }];
  }

  blocks.forEach((block, index) => {
    if (block.type === "image") {
      const image = images[index];
      if (!image) {
        document.setFont("helvetica", "italic");
        document.setFontSize(8.5);
        document.setTextColor(120, 132, 145);
        document.text("Description image could not be loaded.", margin, y);
        y += 7;
        return;
      }

      const maxWidth = contentWidth;
      const maxHeight = 65;
      const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
      const imageWidth = image.width * scale;
      const imageHeight = image.height * scale;
      if (y + imageHeight > pageHeight - 20) {
        y = startNewPage();
      }
      document.addImage(
        image.data,
        "JPEG",
        margin + (contentWidth - imageWidth) / 2,
        y,
        imageWidth,
        imageHeight,
        undefined,
        "FAST"
      );
      y += imageHeight + 5;
      return;
    }

    const lines = wrapRichText(document, block.runs, contentWidth);
    lines.forEach((line) => {
      if (y + 4.5 > pageHeight - 20) {
        y = startNewPage();
      }
      let x = margin;
      line.forEach((run) => {
        document.setFont("helvetica", fontStyle(run));
        document.setFontSize(9.5);
        document.setTextColor(58, 72, 86);
        document.text(run.text, x, y);
        x += document.getTextWidth(run.text);
      });
      y += 4.5;
    });
    y += 1.5;
  });

  setY(y + 3);
};

const drawProductGallery = (document, images, startY, pageWidth, pageHeight, startNewPage) => {
  const availableImages = images.filter(Boolean);
  if (availableImages.length === 0) return startY;

  const margin = 15;
  const width = pageWidth - margin * 2;
  const gap = 4;
  const columns = Math.min(4, availableImages.length);
  const thumbWidth = (width - gap * (columns - 1)) / columns;
  const thumbHeight = 32;
  let y = startY;

  drawSectionHeading(document, "Product Images", margin, y);
  y += 9;

  for (let rowStart = 0; rowStart < availableImages.length; rowStart += columns) {
    if (y + thumbHeight > pageHeight - 20) {
      y = startNewPage() + 5;
      drawSectionHeading(document, "Product Images | Continued", margin, y);
      y += 9;
    }

    availableImages.slice(rowStart, rowStart + columns).forEach((image, column) => {
      const imageX = margin + column * (thumbWidth + gap);
      document.setFillColor(246, 249, 251);
      document.setDrawColor(218, 226, 233);
      document.roundedRect(imageX, y, thumbWidth, thumbHeight, 1.5, 1.5, "FD");
      drawImageContained(
        document,
        image,
        imageX + 1.5,
        y + 1.5,
        thumbWidth - 3,
        thumbHeight - 3
      );
    });
    y += thumbHeight + gap;
  }

  return y + 2;
};

const drawVariantSection = (document, product, currency, exchangeRate, startY, pageWidth, pageHeight, startNewPage) => {
  let y = startY;
  const margin = 15;
  const variants = product.variants || [];

  if (y + 17 > pageHeight - 20) y = startNewPage();
  drawSectionHeading(document, "Available Variants & Pricing", margin, y);
  y += 9;

  if (variants.length === 0) {
    document.setFont("helvetica", "normal");
    document.setFontSize(9);
    document.setTextColor(58, 72, 86);
    document.text("Contact us for available variants and pricing.", margin, y);
    return y + 8;
  }

  const columns = {
    sku: margin,
    options: margin + 42,
    price: pageWidth - margin - 2
  };
  const optionsWidth = columns.price - columns.options - 23;

  const drawTableHeader = () => {
    document.setFillColor(17, 45, 73);
    document.roundedRect(margin, y - 4, pageWidth - margin * 2, 9, 1, 1, "F");
    document.setFont("helvetica", "bold");
    document.setFontSize(7.5);
    document.setTextColor(255, 255, 255);
    document.text("SKU", columns.sku + 2, y + 1.5);
    document.text("VARIANT", columns.options, y + 1.5);
    document.text("PRICE", columns.price, y + 1.5, { align: "right" });
    y += 9;
  };

  drawTableHeader();
  variants.forEach((variant) => {
    const options = (variant.attributes || [])
      .map((attribute) => `${attribute.name}: ${attribute.value}`)
      .join(" | ") || "Standard";
    document.setFont("helvetica", "normal");
    document.setFontSize(7.5);
    const skuLines = document.splitTextToSize(variant.sku || "—", 34);
    const optionLines = document.splitTextToSize(options, optionsWidth);
    const rowHeight = Math.max(8, Math.max(skuLines.length, optionLines.length) * 3.7 + 3);

    if (y + rowHeight > pageHeight - 20) {
      y = startNewPage();
      drawTableHeader();
    }

    const price = Number(variant.price);
    const priceText = Number.isFinite(price)
      ? currency === "INR"
        ? `INR ${Math.ceil(price * exchangeRate)}`
        : `$${Math.ceil(price)}`
      : "On request";

    document.setFont("helvetica", "normal");
    document.setFontSize(7.5);
    document.setTextColor(58, 72, 86);
    document.text(skuLines, columns.sku + 2, y + 1);
    document.text(optionLines, columns.options, y + 1);

    document.setFont("helvetica", "bold");
    document.text(priceText, columns.price, y + 1, { align: "right" });

    y += rowHeight;
    document.setDrawColor(218, 226, 233);
    document.line(margin, y, pageWidth - margin, y);
    y += 2;
  });

  return y + 3;
};

export const downloadProductCatalogue = async (products, currency, exchangeRate) => {
  const { jsPDF } = await import("jspdf");
  const descriptions = products.map((product) => parseRichText(product.description));
  const productImages = await Promise.all(products.map(loadProductImages));
  const descriptionImages = await Promise.all(
    descriptions.map((blocks, index) =>
      loadRichTextImages(blocks, products[index].name)
    )
  );
  const document = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const navy = [17, 45, 73];
  const teal = [0, 153, 170];

  document.setFillColor(...navy);
  document.rect(0, 0, pageWidth, pageHeight, "F");
  document.setFillColor(...teal);
  document.rect(0, 0, 9, pageHeight, "F");
  document.setFont("helvetica", "bold");
  document.setFontSize(17);
  document.setTextColor(255, 255, 255);
  document.text("RK INDUSTRIES INDIA", 22, 39);
  document.setFont("helvetica", "normal");
  document.setFontSize(12);
  document.setTextColor(208, 222, 233);
  document.text("+91 8685933785", 22, 51);
  document.setDrawColor(...teal);
  document.setLineWidth(1.2);
  document.line(22, 67, 88, 67);

  document.setFontSize(30);
  document.text("PRODUCT", 22, 94);
  document.text("CATALOGUE", 22, 108);
  document.setFont("helvetica", "normal");
  document.setFontSize(12);
  document.setTextColor(208, 222, 233);
  document.text(
    document.splitTextToSize(
      "Laboratory, testing and scientific equipment",
      pageWidth - 44
    ),
    22,
    123
  );

  document.setDrawColor(76, 103, 126);
  document.line(22, 145, pageWidth - 22, 145);
  document.setFont("helvetica", "bold");
  document.setFontSize(13);
  document.setTextColor(255, 255, 255);
  document.text(`${products.length} ACTIVE PRODUCTS`, 22, 160);
  document.setFont("helvetica", "normal");
  document.setFontSize(10);
  document.setTextColor(208, 222, 233);
  document.text(`Updated ${new Date().toLocaleDateString()}`, 22, 169);
  document.setFontSize(9);
  document.text("rkindustriesexports.com", 22, pageHeight - 18);

  products.forEach((product, productIndex) => {
    document.addPage();
    addProductPageHeader(document, product);
    const margin = 15;
    const images = productImages[productIndex];

    document.setFont("helvetica", "bold");
    document.setFontSize(8);
    document.setTextColor(...teal);
    document.text(
      String(product.categoryName || "GENERAL EQUIPMENT").toUpperCase(),
      margin,
      39,
      { maxWidth: pageWidth - margin * 2 }
    );

    document.setFont("helvetica", "bold");
    document.setFontSize(16);
    document.setTextColor(...navy);
    const nameLines = document.splitTextToSize(
      product.name || "Unnamed product",
      pageWidth - margin * 2
    );
    document.text(nameLines, margin, 50);

    let y = 50 + nameLines.length * 7 + 5;
    const setY = (nextY) => {
      y = nextY;
    };
    const getY = () => y;
    const startNewPage = () => {
      document.addPage();
      addProductPageHeader(document, product, true);
      return 50;
    };

    y = drawProductGallery(
      document,
      images,
      y,
      pageWidth,
      pageHeight,
      startNewPage
    );
    y = drawVariantSection(
      document,
      product,
      currency,
      exchangeRate,
      y,
      pageWidth,
      pageHeight,
      startNewPage
    );
    setY(y);
    drawTextSection(document, {
      title: "Description",
      blocks: descriptions[productIndex],
      images: descriptionImages[productIndex],
      product,
      pageWidth,
      pageHeight,
      getY,
      setY,
      startNewPage
    });
  });

  const pageCount = document.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    document.setPage(page);
    if (page > 1) {
      drawPageFooter(document, page, pageCount);
    } else {
      document.setFont("helvetica", "normal");
      document.setFontSize(8);
      document.setTextColor(208, 222, 233);
      document.text(`1 / ${pageCount}`, pageWidth - 22, pageHeight - 18, {
        align: "right"
      });
    }
  }

  document.save("rk-industries-catalogue.pdf");
};
